import { CSSProperties } from "react";
import { HStack, Table } from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { Header, flexRender } from "@tanstack/react-table";
import { LegacyFeatures } from "@tanstack/react-table/legacy";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDndContext } from "@dnd-kit/core";
import { RowData } from "../types";

interface SortableTableHeaderProps {
  header: Header<LegacyFeatures, RowData, unknown>;
  isFetching: boolean;
}

export const SortableTableHeader: React.FC<SortableTableHeaderProps> = ({ header, isFetching }) => {
  const isFixedColumn = header.id === "select" || header.id === "configure";

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.id,
    disabled: isFixedColumn,
  });

  const { active } = useDndContext();
  const isActiveDrag = active?.id === header.id;

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : 0,
  };

  const isSorted = header.column.getIsSorted();
  const canSort = header.column.getCanSort();

  let cursor: CSSProperties["cursor"] = isFixedColumn ? "default" : "grab";

  if (isFetching) {
    cursor = "not-allowed";
  } else if (canSort && !isDragging) {
    cursor = "pointer";
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLTableCellElement> = (event) => {
    (listeners as { onKeyDown?: (e: React.KeyboardEvent) => void })?.onKeyDown?.(event as never);

    if (
      event.code === "Enter" &&
      !isFixedColumn &&
      !isDragging &&
      !isActiveDrag &&
      header.column.getCanSort()
    ) {
      header.column.getToggleSortingHandler()?.(event as unknown as never);
    }
  };

  const handleClick: React.MouseEventHandler<HTMLTableCellElement> = (event) => {
    // `detail === 0` is a keyboard-generated click (Space/Enter on a button).
    // Sorting via keyboard is handled in `handleKeyDown` (Enter only) to avoid
    // colliding with the dnd-kit Space-to-pick-up gesture.
    if (event.detail === 0) return;
    if (isDragging || isActiveDrag) return;

    header.column.getToggleSortingHandler()?.(event as unknown as never);
  };

  return (
    <Table.ColumnHeader
      ref={setNodeRef}
      style={style}
      {...(isFixedColumn ? {} : attributes)}
      {...(isFixedColumn ? {} : listeners)}
      onKeyDown={handleKeyDown}
      cursor={cursor}
      onClick={handleClick}
      userSelect="none"
    >
      <HStack alignItems="center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {isSorted === "desc" && <FaChevronDown aria-label="sorted descending" size={16} />}
        {isSorted === "asc" && <FaChevronUp aria-label="sorted ascending" size={16} />}
      </HStack>
    </Table.ColumnHeader>
  );
};
